import { useFormContext } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { BasicInput } from '../../../components/BasicInput'
import { BasicSelect } from '../../../components/BasicSelect'
import { Button } from '../../../components/ui/button'
import { Checkbox } from '../../../components/ui/checkbox'
import { transactionsService } from '../../../../app/services/transactionService'
import { masks } from '../../../../app/utils/masks'
import { useStepValidation } from '../components/useStepValidation'
import { RegisterFormData } from '../components/types'

interface RegisterBankProps {
  onSubmit: () => void
  onBack: () => void
  isPending: boolean
}

const RegisterBank = ({ onSubmit, onBack, isPending }: RegisterBankProps) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, touchedFields, isSubmitted },
  } = useFormContext<RegisterFormData>()

  const isValid = useStepValidation('bank')

  const { data: banksData } = useQuery({
    queryKey: ['banks'],
    queryFn: () => transactionsService.banks(),
    select(data) {
      return data.banks.map(({ label, value }) => ({
        label: `${value} - ${label}`,
        value,
      }))
    },
  })

  const handleFormSubmit = () => {
    onSubmit()
  }

  const accountHolder = watch('bankInfo.accountHolder')
  const termsAccepted = watch('bankInfo.termsAccepted')
  const emailMarketing = watch('bankInfo.emailMarketing')

  const handleTermsClick = () => {
    const termosUrl = import.meta.env.VITE_TERMOS_URL
    if (termosUrl) {
      window.open(termosUrl, '_blank')
    }
  }

  const getError = (field: keyof RegisterFormData['bankInfo']) => {
    return touchedFields.bankInfo?.[field] || isSubmitted
      ? (errors.bankInfo?.[field]?.message as string)
      : undefined
  }

  return (
    <div className="flex flex-col gap-4 text-black03 sm:gap-6">
      <div className="text-center sm:text-left">
        <h2 className="text-xl font-semibold sm:text-2xl">
          Informações Bancárias
        </h2>
        <p className="mt-1 text-xs text-black03 sm:text-sm">
          Informe os dados da sua conta bancária
        </p>
      </div>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-4 sm:gap-6"
      >
        <BasicSelect
          label="Banco"
          {...register('bankInfo.bank')}
          options={banksData || []}
          placeholder="Selecione seu banco"
          error={getError('bank')}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BasicInput
            label="Agência"
            {...register('bankInfo.agency')}
            error={getError('agency')}
            placeholder="0000"
            onInput={(e) => {
              const target = e.target as HTMLInputElement
              target.value = masks.bankAgency(target.value)
            }}
          />

          <BasicInput
            label="Conta"
            {...register('bankInfo.account')}
            error={getError('account')}
            placeholder="00000-0"
            onInput={(e) => {
              const target = e.target as HTMLInputElement
              target.value = masks.bankAccount(target.value)
            }}
          />
        </div>

        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="flex items-start space-x-2 sm:space-x-3">
            <Checkbox
              id="accountHolder"
              checked={accountHolder}
              onCheckedChange={(checked) =>
                setValue('bankInfo.accountHolder', !!checked)
              }
              className="mt-1 flex-shrink-0"
            />
            <label
              htmlFor="accountHolder"
              className="cursor-pointer text-xs leading-relaxed peer-disabled:cursor-not-allowed peer-disabled:opacity-70 sm:text-sm"
            >
              Declaro ser o titular desta conta e que está identificada como
              Conta Corrente.
            </label>
          </div>
          {getError('accountHolder') && (
            <p className="ml-6 text-xs text-red-500 sm:ml-7 sm:text-sm">
              {getError('accountHolder')}
            </p>
          )}

          <div className="flex items-start space-x-2 sm:space-x-3">
            <Checkbox
              id="termsAccepted"
              checked={termsAccepted}
              onCheckedChange={(checked) =>
                setValue('bankInfo.termsAccepted', !!checked)
              }
              className="mt-1 flex-shrink-0"
            />
            <label
              htmlFor="termsAccepted"
              className="cursor-pointer text-xs leading-relaxed peer-disabled:cursor-not-allowed peer-disabled:opacity-70 sm:text-sm"
            >
              Estou de acordo com os{' '}
              <button
                type="button"
                onClick={handleTermsClick}
                className="break-words text-primary underline hover:text-primary/80"
              >
                Termos e condições {import.meta.env.VITE_COMPANY || 'Start'}
              </button>
            </label>
          </div>
          {getError('termsAccepted') && (
            <p className="ml-6 text-xs text-red-500 sm:ml-7 sm:text-sm">
              {getError('termsAccepted')}
            </p>
          )}

          <div className="flex items-start space-x-2 sm:space-x-3">
            <Checkbox
              id="emailMarketing"
              checked={emailMarketing}
              onCheckedChange={(checked) =>
                setValue('bankInfo.emailMarketing', !!checked)
              }
              className="mt-1 flex-shrink-0"
            />
            <label
              htmlFor="emailMarketing"
              className="cursor-pointer text-xs leading-relaxed peer-disabled:cursor-not-allowed peer-disabled:opacity-70 sm:text-sm"
            >
              Autorizo o envio de e-mails promocionais e informativos.
            </label>
          </div>
        </div>

        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="order-2 h-11 flex-1 text-sm sm:order-1 sm:h-12 sm:text-base"
            disabled={isPending}
          >
            Voltar
          </Button>

          <Button
            type="submit"
            disabled={!isValid || isPending}
            className="order-1 h-11 flex-1 bg-primary text-sm text-white hover:bg-primary/90 sm:order-2 sm:h-12 sm:text-base"
          >
            {isPending ? 'Criando conta...' : 'Criar Conta'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default RegisterBank
